import { useState } from 'react';
import { toast } from 'react-toastify';
import { ManagedAccountsApi } from '@/api/clients/managed-accounts/managed-accounts.api';
import useFormValidation from '@/hooks/useFormValidation';
import { SFTP_AUTHENTICATION_MODE } from '../managedAccount.constants';
import { toPositivePayForm } from '../positivePayConfiguration.utils';

export const usePositivePayConfigurationForm = ({
  managedAccountId,
  configuration,
  onSaved,
}) => {
  const [form, setForm] = useState(() => toPositivePayForm(configuration));
  const [passwordConfigured, setPasswordConfigured] = useState(
    configuration?.passwordConfigured ?? false,
  );
  const [privateKeyConfigured, setPrivateKeyConfigured] = useState(
    configuration?.privateKeyConfigured ?? false,
  );
  const [headerAccountConfigured, setHeaderAccountConfigured] = useState(
    configuration?.headerAccountConfigured ?? false,
  );
  const [headerAccountLastFour, setHeaderAccountLastFour] = useState(
    configuration?.headerAccountLastFour ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [hasSavedConfiguration, setHasSavedConfiguration] = useState(
    Boolean(configuration),
  );
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    clearFieldError(name);
  };

  const updateAuthenticationMode = (authenticationMode) => {
    setForm((current) => {
      let password = '';
      let privateKey = '';
      let publicKey = '';
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.PASSWORD) {
        password = current.password;
      }
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.SSH_KEY) {
        privateKey = current.private_key;
        publicKey = current.public_key;
      }

      return {
        ...current,
        authentication_mode: authenticationMode,
        password,
        private_key: privateKey,
        public_key: publicKey,
      };
    });
    clearFieldError('authentication_mode');
    clearFieldError('password');
    clearFieldError('private_key');
    clearFieldError('public_key');
  };

  const updateShouldEncrypt = (shouldEncrypt) => {
    setForm((current) => ({
      ...current,
      should_encrypt: shouldEncrypt,
      pgp_public_key: shouldEncrypt ? current.pgp_public_key : '',
    }));
    clearFieldError('should_encrypt');
    clearFieldError('pgp_public_key');
  };

  const save = async (event) => {
    event.preventDefault();
    clearAllErrors();
    setSaving(true);

    try {
      const response = await ManagedAccountsApi.updatePositivePay(
        managedAccountId,
        { ...form, port: Number(form.port) },
      );
      const saved = response.data.data.configuration;
      setForm(toPositivePayForm(saved));
      setPasswordConfigured(saved.passwordConfigured);
      setPrivateKeyConfigured(saved.privateKeyConfigured);
      setHeaderAccountConfigured(saved.headerAccountConfigured);
      setHeaderAccountLastFour(saved.headerAccountLastFour);
      setHasSavedConfiguration(true);
      onSaved();
      toast.success('Positive Pay configuration saved');
    } catch (error) {
      setBackendErrors(error.response?.data);
      if (!error.response?.data?.message) {
        toast.error('Failed to save Positive Pay configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    fieldErrors,
    generalErrors,
    passwordConfigured,
    privateKeyConfigured,
    headerAccountConfigured,
    headerAccountLastFour,
    saving,
    hasSavedConfiguration,
    updateField,
    updateAuthenticationMode,
    updateShouldEncrypt,
    save,
  };
};
